import { useCallback, useEffect, useMemo, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { decodeRoles, initializeSession } from '../../lib/auth'
import type { Lang } from '../../lib/i18n'
import { CommandHeader } from '../../components/CommandHeader'
import { st } from '../staff/staff-i18n'
import { formatDateTime } from '../orders/format'
import {
  canSeeSettings,
  fetchSettings,
  patchSetting,
  SettingsApiError,
  type IntBounds,
  type QuietHours,
  type ScreensBounds,
  type ScreensValue,
  type SettingItem,
  type WriteoffBounds,
  type WriteoffReason,
} from '../../lib/settings-api'
import { sx, sxOr } from './settings-i18n'

interface Props {
  lang: Lang
  onLangToggle: () => void
  isOnline: boolean
}

// R503 (d): the first settings page, built as a page of sections so later settings land on
// it. A key belongs to the first section whose prefix it starts with; a key no section claims
// is not drawn. The server's registry decides each key's type, value and bounds.
const SECTIONS = [
  { id: 'ring', title: 'sectionRing', prefix: 'ring.' },
  { id: 'writeoff', title: 'sectionWriteoff', prefix: 'stock.writeoff_reasons' },
] as const

const BUTTON_BASE =
  'inline-flex items-center justify-center rounded-md px-4 text-sm font-semibold min-h-[44px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'
const BUTTON_PRIMARY = `${BUTTON_BASE} bg-primary text-primary-foreground hover:bg-primary/90`
const BUTTON_SECONDARY = `${BUTTON_BASE} border border-border bg-card text-foreground hover:bg-secondary`
const BUTTON_REMOVE = `${BUTTON_BASE} border border-destructive/30 bg-card text-destructive hover:bg-destructive-tint`
const INPUT =
  'rounded-md border border-input bg-card px-3 text-sm text-foreground min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed'
const CHECK = 'h-5 w-5 rounded border-input focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'

/** JSON with object keys sorted, so two equal values compare equal whatever their key order. */
function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>
    return `{${Object.keys(obj)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${stableJson(obj[k])}`)
      .join(',')}}`
  }
  return JSON.stringify(value === undefined ? null : value)
}

// ---- drafts: what the owner is editing, per type --------------------------------------------

interface WriteoffRow extends WriteoffReason {
  uid: string
  saved: boolean
}

type Draft =
  | { type: 'int'; text: string }
  | { type: 'quiet_hours'; on: boolean; start: string; end: string }
  | { type: 'screens'; screens: ScreensValue }
  | { type: 'writeoff_reasons'; rows: WriteoffRow[] }

let rowSeq = 0
function nextUid(): string {
  rowSeq += 1
  return `wo-${rowSeq}`
}

function toDraft(item: SettingItem): Draft | null {
  switch (item.type) {
    case 'int':
      return { type: 'int', text: typeof item.value === 'number' ? String(item.value) : '' }
    case 'quiet_hours': {
      const v = item.value as QuietHours | null
      return v ? { type: 'quiet_hours', on: true, start: v.start, end: v.end } : { type: 'quiet_hours', on: false, start: '', end: '' }
    }
    case 'screens': {
      const b = item.bounds as unknown as ScreensBounds
      const v = (item.value ?? {}) as ScreensValue
      const screens: ScreensValue = {}
      for (const s of b.screens) screens[s] = { on: v[s]?.on === true, reasons: [...(v[s]?.reasons ?? [])] }
      return { type: 'screens', screens }
    }
    case 'writeoff_reasons': {
      const v = (Array.isArray(item.value) ? item.value : []) as WriteoffReason[]
      return {
        type: 'writeoff_reasons',
        rows: v.map((r) => ({ uid: nextUid(), code: r.code, label: r.label, noteRequired: r.noteRequired === true, saved: true })),
      }
    }
    default:
      return null
  }
}

type Built = { ok: true; value: unknown } | { ok: false; reason: 'enterNumber' | 'quietNeedsTimes' }

function fromDraft(draft: Draft, item: SettingItem): Built {
  switch (draft.type) {
    case 'int': {
      const text = draft.text.trim()
      const n = Number(text)
      if (text === '' || Number.isNaN(n)) return { ok: false, reason: 'enterNumber' }
      return { ok: true, value: n }
    }
    case 'quiet_hours':
      if (!draft.on) return { ok: true, value: null }
      if (draft.start === '' || draft.end === '') return { ok: false, reason: 'quietNeedsTimes' }
      return { ok: true, value: { start: draft.start, end: draft.end } }
    case 'screens': {
      const b = item.bounds as unknown as ScreensBounds
      const value: ScreensValue = {}
      for (const s of b.screens) {
        const cur = draft.screens[s] ?? { on: false, reasons: [] }
        value[s] = { on: cur.on, reasons: b.reasons.filter((r) => cur.reasons.includes(r)) }
      }
      return { ok: true, value }
    }
    case 'writeoff_reasons':
      return { ok: true, value: draft.rows.map((r) => ({ code: r.code, label: r.label, noteRequired: r.noteRequired })) }
  }
}

type SaveStatus =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'failed'; message: string }

// ---- one key -----------------------------------------------------------------------------

function SettingRow({
  item,
  lang,
  isOnline,
  onSaved,
}: {
  item: SettingItem
  lang: Lang
  isOnline: boolean
  onSaved: (next: SettingItem) => void
}) {
  const [draft, setDraft] = useState<Draft | null>(() => toDraft(item))
  const [status, setStatus] = useState<SaveStatus>({ kind: 'idle' })
  const baseline = useMemo(() => {
    const d = toDraft(item)
    const built = d ? fromDraft(d, item) : null
    return built && built.ok ? stableJson(built.value) : stableJson(item.value)
  }, [item])

  if (!draft) return null
  const testKey = item.key.replace(/\./g, '-')
  const built = fromDraft(draft, item)
  const dirty = !built.ok || stableJson(built.value) !== baseline
  const saving = status.kind === 'saving'
  const canSave = isOnline && dirty && built.ok && !saving

  function change(next: Draft) {
    setDraft(next)
    if (status.kind !== 'saving') setStatus({ kind: 'idle' })
  }

  async function save() {
    // Nothing changed, a value that cannot be built, offline or already saving: no PATCH.
    if (!canSave || !built.ok) return
    setStatus({ kind: 'saving' })
    try {
      const next = await patchSetting(item.key, built.value)
      onSaved(next)
      setDraft(toDraft(next))
      setStatus({ kind: 'saved' })
    } catch (err) {
      let message: string
      if (err instanceof SettingsApiError) {
        // A 400 carries the server's own words, which name the bound.
        if (err.status === 400) message = err.message
        else if (err.status === 403) message = sx(lang, 'error403')
        else if (err.status === 404) message = sx(lang, 'error404')
        else message = sx(lang, 'errorOther')
      } else {
        message = sx(lang, 'errorNetwork')
      }
      setStatus({ kind: 'failed', message })
    }
  }

  let disabledReason: string | null = null
  if (!saving) {
    if (!isOnline) disabledReason = st(lang, 'needsConnection')
    else if (!built.ok) disabledReason = sx(lang, built.reason)
    else if (!dirty) disabledReason = sx(lang, 'noChanges')
  }

  const label = sxOr(lang, `key_${item.key.replace(/\./g, '_')}`, item.key)
  const headingId = `setting-${testKey}-label`

  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-3" data-testid={`setting-${testKey}`} role="group" aria-labelledby={headingId}>
      <div>
        <h3 id={headingId} className="text-sm font-semibold text-foreground">
          {label}
        </h3>
        <p className="text-xs text-muted-foreground" data-testid={`setting-${testKey}-changed`}>
          {item.updatedAt ? sx(lang, 'lastChanged', { time: formatDateTime(item.updatedAt) }) : sx(lang, 'neverChanged')}
        </p>
      </div>

      {draft.type === 'int' && <IntEditor item={item} draft={draft} lang={lang} testKey={testKey} label={label} disabled={saving} onChange={change} />}
      {draft.type === 'quiet_hours' && <QuietHoursEditor draft={draft} lang={lang} testKey={testKey} disabled={saving} onChange={change} />}
      {draft.type === 'screens' && <ScreensEditor item={item} draft={draft} lang={lang} testKey={testKey} disabled={saving} onChange={change} />}
      {draft.type === 'writeoff_reasons' && <WriteoffEditor item={item} draft={draft} lang={lang} testKey={testKey} disabled={saving} onChange={change} />}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => void save()}
          disabled={!canSave}
          aria-busy={saving}
          data-testid={`setting-${testKey}-save`}
          className={BUTTON_PRIMARY}
        >
          {saving ? sx(lang, 'saving') : sx(lang, 'save')}
        </button>
        {disabledReason && (
          <span className="text-xs text-muted-foreground" data-testid={`setting-${testKey}-reason`}>
            {disabledReason}
          </span>
        )}
        {status.kind === 'saved' && (
          <span role="status" className="text-sm font-medium text-sage-ink" data-testid={`setting-${testKey}-saved`}>
            {sx(lang, 'saved')}
          </span>
        )}
      </div>
      {status.kind === 'failed' && (
        <p
          role="alert"
          className="rounded-md border border-destructive/30 bg-destructive-tint px-3 py-2 text-sm text-destructive"
          data-testid={`setting-${testKey}-error`}
        >
          {status.message}
        </p>
      )}
    </div>
  )
}

// ---- editors -----------------------------------------------------------------------------

function IntEditor({
  item,
  draft,
  lang,
  testKey,
  label,
  disabled,
  onChange,
}: {
  item: SettingItem
  draft: Extract<Draft, { type: 'int' }>
  lang: Lang
  testKey: string
  label: string
  disabled: boolean
  onChange: (d: Draft) => void
}) {
  const b = item.bounds as unknown as IntBounds
  const hintId = `setting-${testKey}-range`
  return (
    <div className="flex flex-wrap items-center gap-3">
      <input
        type="number"
        inputMode="numeric"
        step={1}
        min={b.min}
        max={b.max}
        value={draft.text}
        disabled={disabled}
        aria-label={label}
        aria-describedby={hintId}
        data-testid={`setting-${testKey}-input`}
        onChange={(e) => onChange({ type: 'int', text: e.target.value })}
        className={`${INPUT} w-32`}
      />
      <span id={hintId} className="text-xs text-muted-foreground" data-testid={hintId}>
        {sx(lang, 'rangeHint', { min: b.min, max: b.max })}
      </span>
    </div>
  )
}

function QuietHoursEditor({
  draft,
  lang,
  testKey,
  disabled,
  onChange,
}: {
  draft: Extract<Draft, { type: 'quiet_hours' }>
  lang: Lang
  testKey: string
  disabled: boolean
  onChange: (d: Draft) => void
}) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-4" role="radiogroup">
        <label className="inline-flex items-center gap-2 text-sm text-foreground min-h-[44px]">
          <input
            type="radio"
            name={`setting-${testKey}-mode`}
            checked={!draft.on}
            disabled={disabled}
            data-testid={`setting-${testKey}-off`}
            onChange={() => onChange({ ...draft, on: false })}
            className={CHECK}
          />
          {sx(lang, 'quietOff')}
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-foreground min-h-[44px]">
          <input
            type="radio"
            name={`setting-${testKey}-mode`}
            checked={draft.on}
            disabled={disabled}
            data-testid={`setting-${testKey}-on`}
            onChange={() => onChange({ ...draft, on: true })}
            className={CHECK}
          />
          {sx(lang, 'quietOn')}
        </label>
      </div>
      {draft.on && (
        <div className="flex flex-wrap items-center gap-4">
          <label className="inline-flex items-center gap-2 text-sm text-foreground">
            {sx(lang, 'quietStart')}
            <input
              type="time"
              value={draft.start}
              disabled={disabled}
              data-testid={`setting-${testKey}-start`}
              onChange={(e) => onChange({ ...draft, start: e.target.value })}
              className={INPUT}
            />
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-foreground">
            {sx(lang, 'quietEnd')}
            <input
              type="time"
              value={draft.end}
              disabled={disabled}
              data-testid={`setting-${testKey}-end`}
              onChange={(e) => onChange({ ...draft, end: e.target.value })}
              className={INPUT}
            />
          </label>
        </div>
      )}
    </div>
  )
}

function ScreensEditor({
  item,
  draft,
  lang,
  testKey,
  disabled,
  onChange,
}: {
  item: SettingItem
  draft: Extract<Draft, { type: 'screens' }>
  lang: Lang
  testKey: string
  disabled: boolean
  onChange: (d: Draft) => void
}) {
  const b = item.bounds as unknown as ScreensBounds
  function set(screen: string, next: { on: boolean; reasons: string[] }) {
    onChange({ type: 'screens', screens: { ...draft.screens, [screen]: next } })
  }
  return (
    <div className="space-y-3">
      {b.screens.map((screen) => {
        const cur = draft.screens[screen] ?? { on: false, reasons: [] }
        const name = sxOr(lang, `screen_${screen}`, screen)
        return (
          <fieldset key={screen} className="rounded-md border border-border p-3 space-y-1" data-testid={`setting-${testKey}-${screen}`}>
            <legend className="px-1 text-sm font-semibold text-foreground">{name}</legend>
            <button
              type="button"
              role="switch"
              aria-checked={cur.on}
              disabled={disabled}
              data-testid={`setting-${testKey}-${screen}-on`}
              onClick={() => set(screen, { ...cur, on: !cur.on })}
              className={`${BUTTON_SECONDARY} gap-2`}
            >
              <span
                aria-hidden="true"
                className={`inline-block h-3 w-3 rounded-full ${cur.on ? 'bg-sage' : 'bg-border'}`}
              />
              {sx(lang, 'screenOn')}: {cur.on ? sx(lang, 'quietOn') : sx(lang, 'quietOff')}
            </button>
            <p className="text-xs text-muted-foreground">{sx(lang, 'reasonsLegend')}</p>
            <div className="flex flex-wrap gap-x-4">
              {b.reasons.map((reason) => (
                <label key={reason} className="inline-flex items-center gap-2 text-sm text-foreground min-h-[44px]">
                  <input
                    type="checkbox"
                    checked={cur.reasons.includes(reason)}
                    disabled={disabled}
                    data-testid={`setting-${testKey}-${screen}-${reason}`}
                    onChange={() =>
                      set(screen, {
                        ...cur,
                        reasons: cur.reasons.includes(reason) ? cur.reasons.filter((r) => r !== reason) : [...cur.reasons, reason],
                      })
                    }
                    className={CHECK}
                  />
                  {sxOr(lang, `reason_${reason}`, reason)}
                </label>
              ))}
            </div>
          </fieldset>
        )
      })}
    </div>
  )
}

function WriteoffEditor({
  item,
  draft,
  lang,
  testKey,
  disabled,
  onChange,
}: {
  item: SettingItem
  draft: Extract<Draft, { type: 'writeoff_reasons' }>
  lang: Lang
  testKey: string
  disabled: boolean
  onChange: (d: Draft) => void
}) {
  const b = item.bounds as unknown as WriteoffBounds
  const atMax = draft.rows.length >= b.maxItems
  const atMin = draft.rows.length <= b.minItems

  function update(uid: string, patch: Partial<WriteoffRow>) {
    onChange({ type: 'writeoff_reasons', rows: draft.rows.map((r) => (r.uid === uid ? { ...r, ...patch } : r)) })
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">{sx(lang, 'woCodeFixed')}</p>
      <ul className="space-y-2">
        {draft.rows.map((row, i) => (
          <li
            key={row.uid}
            className="flex flex-wrap items-center gap-3 rounded-md border border-border p-2"
            data-testid={`setting-${testKey}-row-${i}`}
          >
            {row.saved ? (
              <span className="min-w-[8rem] text-sm font-mono text-muted-foreground" data-testid={`setting-${testKey}-row-${i}-code`}>
                {row.code}
              </span>
            ) : (
              <input
                type="text"
                value={row.code}
                disabled={disabled}
                pattern={b.codePattern}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                aria-label={sx(lang, 'woCode')}
                placeholder={sx(lang, 'woCode')}
                data-testid={`setting-${testKey}-row-${i}-code-input`}
                onChange={(e) => update(row.uid, { code: e.target.value })}
                className={`${INPUT} w-36 font-mono`}
              />
            )}
            <input
              type="text"
              value={row.label}
              disabled={disabled}
              maxLength={b.labelMaxLength}
              aria-label={sx(lang, 'woLabel')}
              placeholder={sx(lang, 'woLabel')}
              data-testid={`setting-${testKey}-row-${i}-label`}
              onChange={(e) => update(row.uid, { label: e.target.value })}
              className={`${INPUT} flex-1 min-w-[10rem]`}
            />
            <label className="inline-flex items-center gap-2 text-sm text-foreground min-h-[44px]">
              <input
                type="checkbox"
                checked={row.noteRequired}
                disabled={disabled}
                data-testid={`setting-${testKey}-row-${i}-note`}
                onChange={() => update(row.uid, { noteRequired: !row.noteRequired })}
                className={CHECK}
              />
              {sx(lang, 'woNoteRequired')}
            </label>
            <button
              type="button"
              disabled={disabled || atMin}
              data-testid={`setting-${testKey}-row-${i}-remove`}
              onClick={() => onChange({ type: 'writeoff_reasons', rows: draft.rows.filter((r) => r.uid !== row.uid) })}
              className={BUTTON_REMOVE}
            >
              {sx(lang, 'woRemove')}
            </button>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={disabled || atMax}
          data-testid={`setting-${testKey}-add`}
          onClick={() =>
            onChange({
              type: 'writeoff_reasons',
              rows: [...draft.rows, { uid: nextUid(), code: '', label: '', noteRequired: false, saved: false }],
            })
          }
          className={BUTTON_SECONDARY}
        >
          {sx(lang, 'woAdd')}
        </button>
        {atMax && (
          <span className="text-xs text-muted-foreground" data-testid={`setting-${testKey}-at-max`}>
            {sx(lang, 'woAtMax', { max: b.maxItems })}
          </span>
        )}
        {atMin && (
          <span className="text-xs text-muted-foreground" data-testid={`setting-${testKey}-at-min`}>
            {sx(lang, 'woAtMin', { min: b.minItems })}
          </span>
        )}
      </div>
    </div>
  )
}

// ---- the screen --------------------------------------------------------------------------

type AuthState = 'checking' | 'ok' | 'not-owner' | 'redirect'
type LoadState = 'loading' | 'ready' | 'error' | 'forbidden'

export function SettingsScreen({ lang, onLangToggle, isOnline }: Props) {
  const [authState, setAuthState] = useState<AuthState>('checking')
  const [loadState, setLoadState] = useState<LoadState>('loading')
  const [items, setItems] = useState<SettingItem[]>([])

  useEffect(() => {
    initializeSession().then((session) => {
      if (!session.authenticated) return setAuthState('redirect')
      setAuthState(canSeeSettings(decodeRoles()) ? 'ok' : 'not-owner')
    })
  }, [])

  const load = useCallback(() => {
    setLoadState('loading')
    fetchSettings()
      .then((data) => {
        setItems(data.settings)
        setLoadState('ready')
      })
      .catch((err) => {
        setLoadState(err instanceof SettingsApiError && err.status === 403 ? 'forbidden' : 'error')
      })
  }, [])

  useEffect(() => {
    if (authState === 'ok') load()
  }, [authState, load])

  const onSaved = useCallback((next: SettingItem) => {
    setItems((prev) => prev.map((it) => (it.key === next.key ? next : it)))
  }, [])

  if (authState === 'checking') return null
  if (authState === 'redirect') return <Navigate to="/login" replace />

  const dir = lang === 'ar' ? 'rtl' : 'ltr'

  return (
    <div dir={dir} className="min-h-screen bg-background font-sans">
      <CommandHeader title={sx(lang, 'title')} lang={lang} onLangToggle={onLangToggle} isOnline={isOnline} />
      <main className="px-4 py-6 max-w-2xl mx-auto pb-10 space-y-6">
        {(authState === 'not-owner' || loadState === 'forbidden') && (
          <p className="rounded-md border border-border bg-card px-4 py-6 text-center text-sm text-foreground" data-testid="settings-owner-only">
            {sx(lang, 'ownerOnly')}
          </p>
        )}

        {authState === 'ok' && loadState === 'loading' && (
          <p role="status" className="text-sm text-muted-foreground py-8 text-center" data-testid="settings-loading">
            {sx(lang, 'loading')}
          </p>
        )}

        {authState === 'ok' && loadState === 'error' && (
          <div
            role="alert"
            className="rounded-md border border-destructive/30 bg-destructive-tint px-4 py-3 text-sm text-destructive flex flex-wrap items-center justify-between gap-2"
            data-testid="settings-load-error"
          >
            <span>{sx(lang, 'loadFailed')}</span>
            <button type="button" onClick={load} className={`${BUTTON_SECONDARY} text-destructive`} data-testid="settings-retry">
              {st(lang, 'retry')}
            </button>
          </div>
        )}

        {authState === 'ok' &&
          loadState === 'ready' &&
          SECTIONS.map((section) => {
            const own = items.filter((it) => it.key.startsWith(section.prefix))
            if (own.length === 0) return null
            return (
              <section key={section.id} className="space-y-3" data-testid={`settings-section-${section.id}`}>
                <h2 className="text-lg font-semibold text-foreground">{sx(lang, section.title)}</h2>
                {own.map((item) => (
                  <SettingRow key={item.key} item={item} lang={lang} isOnline={isOnline} onSaved={onSaved} />
                ))}
              </section>
            )
          })}
      </main>
    </div>
  )
}
